import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Save,
  Check,
  Facebook,
  Instagram,
  Youtube,
  Globe,
  Building2,
  AlertCircle,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  get_all_WebsiteManagement,
  save_update_WebsiteManagement,
} from "../ServiceConnection/serviceconnection.js";


const EMPTY_FORM = {
  phone: "", // website_contact_no_first
  phoneAlt: "", // website_contact_no_second
  email: "", // website_email
  address: "", // website_address1 — Shop/Pickup
  officeAddress: "", // website_address  — Office
  facebook: "", // facebook_link
  instagram: "", // instragram_link (old typo kept as-is)
  twitter: "", // twitter_link
  youtube: "", // youtube_link
  termsCondition: "", // terms_condition
  privacyPolicy: "", // privacy_policy
};

const TABS = [
  { key: "contact", label: "Contact", icon: Phone },
  { key: "address", label: "Address", icon: MapPin },
  { key: "social", label: "Social", icon: Globe },
  { key: "legal", label: "Legal", icon: AlertCircle },
];

const SOCIAL_FIELDS = [
  {
    formKey: "facebook",
    apiName: "facebook_link",
    label: "Facebook",
    icon: Facebook,
    placeholder: "https://www.facebook.com/yourpage",
    color: "text-blue-600",
  },
  {
    formKey: "instagram",
    apiName: "instragram_link", // old typo kept as-is from backend
    label: "Instagram",
    icon: Instagram,
    placeholder: "https://www.instagram.com/yourhandle",
    color: "text-pink-500",
  },
  {
    formKey: "youtube",
    apiName: "youtube_link",
    label: "YouTube",
    icon: Youtube,
    placeholder: "https://www.youtube.com/@yourchannel",
    color: "text-red-500",
  },
  {
    formKey: "twitter",
    apiName: "twitter_link",
    label: "Twitter / X",
    icon: Globe,
    placeholder: "https://twitter.com/yourhandle",
    color: "text-sky-500",
  },
];

// ─── Section wrapper ──────────────────────────────────────────────────────────
const Section = ({
  icon: Icon,
  title,
  subtitle,
  children,
  iconColor = "text-primary",
  iconBg = "bg-primary/10",
}) => (
  <Card>
    <div className="flex items-center gap-3 border-b border-border px-6 py-4">
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-xl ${iconBg}`}
      >
        <Icon className={`h-4 w-4 ${iconColor}`} />
      </div>
      <div>
        <h3 className="text-sm font-bold text-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
    </div>
    <CardContent className="p-6">{children}</CardContent>
  </Card>
);

// ─── Field wrapper ────────────────────────────────────────────────────────────
const Field = ({ label, required, error, children }) => (
  <div>
    <label className="mb-1.5 block text-xs font-semibold text-foreground">
      {label} {required && <span className="text-destructive">*</span>}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
  </div>
);

// ─── Input with icon prefix ───────────────────────────────────────────────────
const IconInput = ({
  icon: Icon,
  iconColor = "text-muted-foreground",
  ...props
}) => (
  <div className="relative">
    <Icon
      className={`absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${iconColor}`}
    />
    <Input className="pl-9" {...props} />
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
const Settings = () => {
  const { toast } = useToast();

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState("contact");

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      const fd = new FormData();
      fd.append("flag", "1");

      const response = await server_post_data(get_all_WebsiteManagement, fd);

      if (!response.data.error) {
        const data = response.data.message.data_websiteManagement?.[0] || {};
        setForm({
          phone: data.website_contact_no_first || "",
          phoneAlt: data.website_contact_no_second || "",
          email: data.website_email || "",
          address: data.website_address1 || "", // Shop/Pickup
          officeAddress: data.website_address || "", // Office
          facebook: data.facebook_link || "",
          instagram: data.instragram_link || "", // typo kept
          twitter: data.twitter_link || "",
          youtube: data.youtube_link || "",
          termsCondition: data.terms_condition || "",
          privacyPolicy: data.privacy_policy || "",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const set = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.phone.trim()) e.phone = "Primary phone is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email address";
    if (!form.address.trim()) e.address = "Shop address is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return;

    try {
      setShowLoaderAdmin(true);

      const fd = new FormData();
      // existing backend fields — exact param names from AdminWebsite model
      fd.append("phone_no", form.phone);
      fd.append("other_no", form.phoneAlt);
      fd.append("email_id", form.email);
      fd.append("address1", form.address); // website_address1 (Shop)
      fd.append("address", form.officeAddress); // website_address  (Office)
      fd.append("facebook_link", form.facebook);
      fd.append("instragram_link", form.instagram); // typo kept as-is
      fd.append("twitter_link", form.twitter);
      fd.append("youtube_link", form.youtube);
      fd.append("terms_condition", form.termsCondition);
      fd.append("privacy_policy", form.privacyPolicy);

      const response = await server_post_data(
        save_update_WebsiteManagement,
        fd,
      );

      if (!response.data.error) {
        toast({
          title: "Success",
          description: response.data.message,
        });
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
        fetchData();
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  };

  // ─── Derived ──────────────────────────────────────────────────────────────
  const hasErrors = Object.values(errors).some(Boolean);

  // ─── UI ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">
            Website Settings
          </h2>
          <p className="text-sm text-muted-foreground">
            Update the contact info displayed across your website.
          </p>
        </div>
        <Button
          size="sm"
          disabled={showLoaderAdmin}
          className={`gap-2 rounded-xl transition-all ${
            saved ? "bg-success hover:bg-success" : ""
          }`}
          onClick={handleSave}
        >
          {showLoaderAdmin ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Saving...
            </>
          ) : saved ? (
            <>
              <Check className="h-4 w-4" /> Saved!
            </>
          ) : (
            <>
              <Save className="h-4 w-4" /> Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Error banner */}
      {hasErrors && (
        <div className="flex items-start gap-3 rounded-2xl border border-destructive/20 bg-destructive/5 p-4">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div>
            <p className="text-sm font-semibold text-destructive">
              Please fix the errors below before saving.
            </p>
            <ul className="mt-1 space-y-0.5">
              {Object.entries(errors)
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <li key={k} className="text-xs text-destructive">
                    {v}
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-4">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-all ${
              activeTab === tab.key
                ? "bg-gradient-primary text-white shadow-glow"
                : "border border-border text-muted-foreground hover:border-primary/30 hover:text-primary"
            }`}
          >
            <tab.icon className="h-3.5 w-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── CONTACT TAB ───────────────────────────────────────────────── */}
      {activeTab === "contact" && (
        <Section
          icon={Phone}
          title="Contact Information"
          subtitle="Phone numbers and email shown on your website and footer"
        >
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-3">
              {/* phone_no → website_contact_no_first */}
              <Field label="Primary Phone" required error={errors.phone}>
                <IconInput
                  icon={Phone}
                  iconColor="text-primary"
                  name="phone_no"
                  placeholder="(847) 250-7280"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className={errors.phone ? "border-destructive pl-9" : "pl-9"}
                />
              </Field>

              {/* other_no → website_contact_no_second */}
              <Field label="Alternate Phone">
                <IconInput
                  icon={Phone}
                  name="other_no"
                  placeholder="Alt. Phone Number"
                  value={form.phoneAlt}
                  onChange={(e) => set("phoneAlt", e.target.value)}
                />
              </Field>

              {/* email_id → website_email */}
              <Field label="Email Address" required error={errors.email}>
                <IconInput
                  icon={Mail}
                  iconColor="text-primary"
                  name="email_id"
                  type="email"
                  placeholder="info@example.com"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className={errors.email ? "border-destructive pl-9" : "pl-9"}
                />
              </Field>
            </div>

            {/* Preview */}
            <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Preview — Footer & Header
              </p>
              <div className="flex flex-wrap gap-6 text-sm">
                <span className="flex items-center gap-2 text-foreground">
                  <Phone className="h-4 w-4 text-primary" />
                  {form.phone || "—"}
                </span>
                {form.phoneAlt && (
                  <span className="flex items-center gap-2 text-foreground">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    {form.phoneAlt}
                  </span>
                )}
                <span className="flex items-center gap-2 text-foreground">
                  <Mail className="h-4 w-4 text-primary" />
                  {form.email || "—"}
                </span>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* ── ADDRESS TAB ───────────────────────────────────────────────── */}
      {activeTab === "address" && (
        <Section
          icon={MapPin}
          title="Shop Address"
          subtitle="Physical address shown in footer, contact page, and map section"
          iconColor="text-blue-500"
          iconBg="bg-blue-500/10"
        >
          <div className="space-y-5">
            {/* address1 → website_address1 (Shop / Pickup) */}
            <Field
              label="Shop / Pickup Address"
              required
              error={errors.address}
            >
              <Textarea
                name="address1"
                placeholder="1141 Lee St, Des Plaines, IL 60016"
                rows={3}
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
                className={errors.address ? "border-destructive" : ""}
              />
            </Field>

            {/* address → website_address (Office) */}
            <Field label="Office / Mailing Address (optional)">
              <Textarea
                name="address"
                placeholder="1400 Renaissance Dr, Park Ridge, IL 60068"
                rows={3}
                value={form.officeAddress}
                onChange={(e) => set("officeAddress", e.target.value)}
              />
            </Field>

            {/* Preview */}
            <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Preview — Footer
              </p>
              <div className="space-y-1 text-sm">
                <span className="flex items-start gap-2 text-foreground">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  {form.address || "—"}
                </span>
                {form.officeAddress && (
                  <span className="flex items-start gap-2 text-muted-foreground">
                    <Building2 className="mt-0.5 h-4 w-4 shrink-0" />
                    {form.officeAddress}
                  </span>
                )}
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* ── SOCIAL TAB ────────────────────────────────────────────────── */}
      {activeTab === "social" && (
        <Section
          icon={Globe}
          title="Social Media"
          subtitle="Links shown in the footer and contact sections"
          iconColor="text-purple-500"
          iconBg="bg-purple-500/10"
        >
          <div className="space-y-4">
            {SOCIAL_FIELDS.map((social) => (
              <Field key={social.formKey} label={social.label}>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <social.icon
                      className={`absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${social.color}`}
                    />
                    <Input
                      name={social.apiName}
                      placeholder={social.placeholder}
                      value={form[social.formKey]}
                      onChange={(e) => set(social.formKey, e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {form[social.formKey] && (
                    <a
                      href={form[social.formKey]}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </a>
                  )}
                </div>
              </Field>
            ))}

            {/* Preview */}
            <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Active Social Links
              </p>
              <div className="flex flex-wrap gap-2">
                {SOCIAL_FIELDS.filter((s) => form[s.formKey]).map((s) => (
                  <a
                    key={s.formKey}
                    href={form[s.formKey]}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/30"
                  >
                    <s.icon className={`h-3.5 w-3.5 ${s.color}`} />
                    {s.label}
                    <ExternalLink className="h-3 w-3 text-muted-foreground" />
                  </a>
                ))}
                {!SOCIAL_FIELDS.some((s) => form[s.formKey]) && (
                  <p className="text-xs italic text-muted-foreground">
                    No social links added yet.
                  </p>
                )}
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* ── LEGAL TAB ─────────────────────────────────────────────────── */}
      {activeTab === "legal" && (
        <Section
          icon={AlertCircle}
          title="Legal Content"
          subtitle="Terms & Conditions and Privacy Policy pages"
          iconColor="text-orange-500"
          iconBg="bg-orange-500/10"
        >
          <div className="space-y-5">
            {/* terms_condition */}
            <Field label="Terms & Conditions">
              <Textarea
                name="terms_condition"
                placeholder="Enter your full terms and conditions text here..."
                rows={8}
                value={form.termsCondition}
                onChange={(e) => set("termsCondition", e.target.value)}
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                {form.termsCondition?.length || 0} characters
              </p>
            </Field>

            {/* privacy_policy */}
            <Field label="Privacy Policy">
              <Textarea
                name="privacy_policy"
                placeholder="Enter your full privacy policy text here..."
                rows={8}
                value={form.privacyPolicy}
                onChange={(e) => set("privacyPolicy", e.target.value)}
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                {form.privacyPolicy?.length || 0} characters
              </p>
            </Field>
          </div>
        </Section>
      )}

      {/* Save button at bottom */}
      <div className="flex justify-end border-t border-border pt-4">
        <Button
          disabled={showLoaderAdmin}
          className={`gap-2 rounded-xl transition-all ${
            saved ? "bg-success hover:bg-success" : ""
          }`}
          onClick={handleSave}
        >
          {showLoaderAdmin ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Saving...
            </>
          ) : saved ? (
            <>
              <Check className="h-4 w-4" /> All Changes Saved!
            </>
          ) : (
            <>
              <Save className="h-4 w-4" /> Save All Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Settings;
